<template>
	<div class="achievement-box-wrapper">
		<div
			class="achievement-box"
			:class="{
				'achievement-box--unclaimed':
					achievement.state === AchievementStates.UNCLAIMED,
				'achievement-box--claimed':
					achievement.state === AchievementStates.CLAIMED,
			}"
		>
			<AppNotification
				v-if="achievement.state === AchievementStates.UNCLAIMED"
				value="!"
				:type="StatusTypes.WARNING"
			/>
			<div
				class="achievement-box__icon"
				:class="`icon-achievement-${achievement.id}-64`"
			/>
			<div class="achievement-box__content">
				<div class="achievement-box__title" v-if="achievement.title">
					{{ translate(achievement.title) }}
					<span
						class="achievement-box__title--claimed"
						v-if="achievement.state === AchievementStates.CLAIMED"
					>
						{{ translate("ui.claimed") }}
					</span>
				</div>
				<div
					class="achievement-box__description"
					v-if="achievement.description && achievement.target_value"
				>
					{{
						translate(achievement.description).replace(
							"{number}",
							formatNumber(achievement.target_value),
						)
					}}
				</div>
				<div
					class="achievement-box__progress"
					v-if="achievement.target_value"
				>
					{{
						` ${formatNumber(achievement.progress)}/${formatNumber(achievement.target_value)}`
					}}
					<span
						v-if="achievement.state === AchievementStates.CLAIMED"
						class="icon-check"
					/>
				</div>
				<div class="achievement-box__reward" v-if="achievement.reward">
					<span>{{ `${translate("ui.reward")}: ` }}</span>
					<AppPrice
						:amount="achievement.reward.amount"
						:currency="achievement.reward.reward_resource"
						:iconSize="14"
						:fontSize="14"
					/>
				</div>
			</div>
		</div>
		<div
			class="achievement-box--claim-label"
			v-if="achievement.state === AchievementStates.UNCLAIMED"
		>
			<div class="achievement-box--claim-label__content">
				<span class="achievement-box--claim-label__text">{{
					translate("ui.click_to_claim")
				}}</span>
			</div>
			<BidirectionalHalfLabelBackground
				class="achievement-box--claim-label__background"
			/>
		</div>
	</div>
</template>
<script lang="ts" setup>
import AppNotification from "@/components/AppNotification.vue";
import AppPrice from "@/components/AppPrice.vue";
import BidirectionalHalfLabelBackground from "@/components/SVG/BidirectionalHalfLabelBackground.vue";
import { AchievementStates } from "@/game/entities/config/achievements/achievement_states";
import type { Achievement } from "@/types/achievements";
import { StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { translate } from "@/utils/translate";

interface Props {
	achievement: Achievement;
}

const { achievement } = defineProps<Props>();
</script>
<style lang="scss" scoped>
.achievement-box {
	display: flex;
	position: relative;
	flex-direction: row;
	gap: 0.5rem;
	box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
	border-width: 0.05rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #61667f, #787c8b);
	border-image-slice: 1;
	background-image: linear-gradient(to top, #3b3c48, #595d6d);
	padding: 0.3rem;
	width: 100%;
	:deep(.notification) {
		position: absolute;
		top: -0.469rem;
		right: -0.469rem;
	}
	&-wrapper {
		width: 100%;
		max-width: 25rem;
		@include clickable-element;
		@include flex-col-center;
	}
	&__content {
		width: 100%;
	}
	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		// NOTE: Color-burn blending is better for dark colors
		mix-blend-mode: color-burn;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		content: "";
	}
	&--unclaimed {
		box-shadow:
			0 0 1rem 0 rgba($color: #bd9032, $alpha: 0.8),
			inset 0 0 0.5rem 0 rgba($color: #bd9032, $alpha: 0.6);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #1d6130, #2bad50);
	}
	&--claimed {
		box-shadow: inset 0 0 0.75rem 0 rgba(21, 87, 39, 0.84);
		border-width: 0.063rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #1d6130, #2bad50);
		border-image-slice: 1;
		.achievement-box__reward {
			opacity: 0.6;
		}
	}
	&__icon {
		flex-shrink: 0;
	}
	&__title {
		display: flex;
		justify-content: space-between;
		color: #fff3dd;
		font-weight: 800;
		font-size: 1rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		&--claimed {
			margin-right: 0.25rem;
			color: #19b345;
			font-weight: 800;
			font-size: 1.063rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
	}
	&__description {
		color: #fff3dd;
		font-size: 0.813rem;
	}
	&__progress {
		color: #f2dcb5;
		font-weight: 800;
		font-size: 0.906rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

		:deep(.icon-check) {
			display: inline-block;
			margin-top: 0.25rem;
			margin-left: 0.25rem;
			width: 0.813rem;
			height: 0.813rem;
		}
	}
	&__reward {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		color: #f4d278;
		font-size: 0.875rem;
		:deep(.price) {
			span {
				color: #f4d278;
			}
		}
	}
	&--claim-label {
		display: flex;
		position: relative;
		width: 22rem;

		&__content {
			position: absolute;
			top: 0;
			left: 0;
			z-index: 1;
			width: 100%;
			height: 100%;
			@include flex-col-center;
		}

		&__text {
			display: block;
			position: relative;
			padding-bottom: 0.5rem;
			width: 85%;
			overflow: hidden;
			color: #fff3dd;
			font-weight: 800;
			font-size: 0.875rem;
			line-height: 0.875rem;
			text-align: center;
			text-overflow: ellipsis;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
			white-space: nowrap;
		}

		&__background {
			position: relative;
			z-index: 0;
			width: 100%;
		}
	}
}
</style>
